import {
  action,
  computed,
  makeObservable,
  observable,
  ObservableMap,
  runInAction,
} from 'mobx';
import RootStore from './RootStore';
import {
  AttachmentTypeEnum,
  IMessage,
  IPatchMessagesReadBody,
} from '../interfaces/message.interface';
import { inquiryIdToThreadId } from '../utils/string';
import { utcMoment } from '../utils/date';
import { MessageApi } from '../api/message.api';
import { IInquiry, IInquiryTarget } from '../interfaces/inquiry.interface';
import { IUser } from '../interfaces/user.interface';

export default class MessageStore {
  private rootStore: RootStore;
  public WAIT_MILLISECOND_FETCH_MESSAGES = 300;

  @observable messages: ObservableMap<string, IMessage> = observable.map();

  @action
  initializeStore = () => {
    this.messages = observable.map();
  };

  @computed
  get messagesCount(): number {
    return Array.from(this.messages).length;
  }

  @computed
  get sortedMessages(): IMessage[] {
    return Array.from(this.messages)
      .sort((a: [string, IMessage], b: [string, IMessage]): number => {
        return a[1].messageSentTimestamp < b[1].messageSentTimestamp ? -1 : 1;
      })
      .map((messageMapArray: [string, IMessage]) => {
        return messageMapArray[1];
      });
  }

  @action
  fetchMessageData = async (activeInquiryId: string) => {
    console.log('MessageStore:fetchMessageData');

    this.initializeStore();
    this.rootStore.inquiryPageStore.setIsLoadingMessageData(true);
    try {
      const res = await MessageApi.getMessages(
        inquiryIdToThreadId(activeInquiryId),
        activeInquiryId,
        {},
      );
      runInAction(() => {
        res.messages.records.forEach((message) => {
          this.messages.set(message.messageSentTimestamp, message);
        });
      });

      this.rootStore.inquiryPageStore.setIsLoadingMessageData(false);

      setTimeout(() => {
        this.rootStore.inquiryPageStore.lastMessageDivScrollIntoView();
      }, this.WAIT_MILLISECOND_FETCH_MESSAGES); // to wait fetch and render all messages, it needs to wait longer than the others

      await this.updateMessageReadStatus();

      this.rootStore.inquiryPageStore.focusChatInput();

      return { inquiry: res.inquiry, recipients: res.recipients };
    } catch (e: any) {
      console.error(e);
      // silent transition when no inquiry found with the user id
      // the transition case is for visiting inquiry detail with different user id
      // at the other cases, it navigates to error page
      if (
        e.status === 500 ||
        e.status === 404 /* BadRequestException or else*/
      ) {
        this.rootStore.routerStore.push(`/inquiry`);
        await this.rootStore.inquiryStore.fetchInquiryList();
      } else {
        this.rootStore.inquiryPageStore.setFailedLoadingInquiryData(true);
      }
      return { inquiry: null, recipients: null };
    }
  };

  @action
  postMessageData = async () => {
    const { user, inquiryTarget } = this.rootStore.userStore;
    const { activeInquiryId, activeInquiry } = this.rootStore.inquiryStore;
    const { inputMessage } = this.rootStore.chatInputStore;
    const attachments = this.rootStore.chatInputStore.attachments.map(
      (attachment) => {
        const types = [AttachmentTypeEnum.original];
        if (attachment.hasThumbnail) {
          types.push(AttachmentTypeEnum.thumbnail);
        }
        return {
          filePath: attachment.fileId,
          fileName: attachment.fileName,
          types,
        };
      },
    );
    if (!inputMessage) {
      return;
    }

    try {
      !activeInquiry
        ? await this.createNewInquiryAndTransitionPage(
            Array.from(this.rootStore.userStore.recipients.values()).map(
              (e) => e.userId,
            ),
            inputMessage,
            attachments,
            user,
            inquiryTarget,
          )
        : await this.sendMessageOnExistingInquiry(
            activeInquiryId,
            activeInquiry,
            inputMessage,
            attachments,
            user,
          );
    } catch (e) {
      console.error(e);
      this.rootStore.inquiryPageStore.setFailedLoadingInquiryData(true);
    }
  };

  private async sendMessageOnExistingInquiry(
    activeInquiryId: string,
    activeInquiry: IInquiry | null,
    inputMessage: string,
    attachments: {
      fileName: string;
      types: AttachmentTypeEnum[];
      filePath: string;
    }[],
    user: IUser,
  ) {
    const message = await MessageApi.postMessage({
      threadId: inquiryIdToThreadId(activeInquiryId),
      inquiryId: activeInquiryId,
      recipients: activeInquiry!.recipients.map(
        (recipient) => recipient.userId,
      ),
      messageBody: inputMessage,
      attachments,
      serviceAttribute: user.serviceAttribute,
      serviceId: user.serviceId,
    });

    runInAction(() => {
      this.messages.set(message.messageSentTimestamp, message);
    });
    await this.rootStore.inquiryStore.onMessageSent(
      message.inquiryId,
      message.messageBody,
      message.messageSentTimestamp,
    );
    setTimeout(() => {
      this.rootStore.inquiryPageStore.lastMessageDivScrollIntoView('smooth');
    }, 100);
  }

  private async createNewInquiryAndTransitionPage(
    recipientUserIds: Array<string>,
    inputMessage: string,
    attachments: {
      fileName: string;
      types: AttachmentTypeEnum[];
      filePath: string;
    }[],
    user: IUser,
    inquiryTarget: IInquiryTarget,
  ) {
    const message = await MessageApi.postMessage({
      recipients: recipientUserIds,
      messageBody: inputMessage,
      attachments,
      serviceId: user.serviceId,
      serviceAttribute: user.serviceAttribute,
      categoryId: inquiryTarget.categoryId,
      inquiryCategory:
        this.rootStore.faqStore.inputSelectionMap.inquiryCategory,
      inquirySubCategory:
        this.rootStore.faqStore.inputSelectionMap.inquirySubCategory,
      targetName: inquiryTarget.targetName,
      targetId: inquiryTarget.targetId,
      targetUrl: inquiryTarget.targetUrl,
    });
    this.rootStore.inquiryStore.setActiveInquiryId(message.inquiryId);
    this.rootStore.inquiryStore.updateInquiryLastReadStatus(message.inquiryId);

    // TODO: only for new inquiry page..., confusing....
    this.rootStore.socketStore.emitWatchingState('focus');
  }

  @action
  updateMessageReadStatus = async () => {
    const { user } = this.rootStore.userStore;
    const { activeInquiryId } = this.rootStore.inquiryStore;
    const threadId = inquiryIdToThreadId(activeInquiryId);
    const unreadMessages: Array<IMessage> = Array.from(this.messages)
      .filter((messageArray: [string, IMessage]) => {
        return (
          messageArray[1].messageSender !== user.userId &&
          !messageArray[1].messageReadTimestamp
        );
      })
      .map((messageArray: [string, IMessage]) => messageArray[1]);
    const messageSentTimestampsMap: Record<string, string[]> = {};
    unreadMessages.forEach((m) => {
      if (!messageSentTimestampsMap[m.messageSender]) {
        messageSentTimestampsMap[m.messageSender] = [];
      }
      messageSentTimestampsMap[m.messageSender].push(m.messageSentTimestamp);
    });
    if (Object.keys(messageSentTimestampsMap).length > 0) {
      try {
        for (const messageSender of Object.keys(messageSentTimestampsMap)) {
          const messageSentTimestamps = messageSentTimestampsMap[messageSender];
          const body: IPatchMessagesReadBody = {
            messageSentTimestamps,
            messageSender,
          };
          await MessageApi.readMessages(threadId, activeInquiryId, body);
          runInAction(() => {
            messageSentTimestamps.forEach((messageSentTimestamp) => {
              this.messages.set(messageSentTimestamp, {
                ...this.messages.get(messageSentTimestamp)!,
                messageReadTimestamp: utcMoment().format(
                  'YYYY-MM-DDTHH:mm:ss.SSSZZ',
                ),
              });
            });
          });
        }
      } catch (e: any) {
        console.error(e);
        // do nothing
      }
      this.rootStore.inquiryStore.updateInquiryLastReadStatus(activeInquiryId);
    }
  };

  // for future expansion
  // @action
  // readMessagesByRecipients = (messageReadTimestamp: string | null) => {
  //   const { user } = this.rootStore.userStore;
  //   const messageSentTimestamps: Array<string> = Array.from(this.messages)
  //     .filter((messageArray: [string, IMessage]) => {
  //       return (
  //         messageArray[1].messageSender === user.userId &&
  //         !messageArray[1].messageReadTimestamp
  //       );
  //     })
  //     .map((messageArray: [string, IMessage]) => {
  //       return messageArray[1].messageSentTimestamp;
  //     });
  //   runInAction(() => {
  //     messageSentTimestamps.forEach((messageSentTimestamp) => {
  //       this.messages.set(messageSentTimestamp, {
  //         ...this.messages.get(messageSentTimestamp)!,
  //         messageReadTimestamp:
  //           messageReadTimestamp ||
  //           utcMoment().format('YYYY-MM-DDTHH:mm:ss.SSSZZ'),
  //       });
  //     });
  //   });
  // };

  constructor(rootStore: RootStore) {
    console.log('MessageStore:constructor');
    makeObservable(this);
    this.rootStore = rootStore;
  }
}
